/**
 * Public invite-preview hook for the `/invites/accept?token=…` landing page
 * (task 104d.2 D2.2 / D2.5). Mirrors the mockup-mode payload structure from
 * `src/data/invitePreview.ts` so the page component consumes the same shape
 * in both modes.
 *
 * Calls `GET /api/v1/invites/:token/preview` — unauthenticated on the
 * backend (the Clerk plugin exempts the path). Passing `token = null`
 * disables the query so the page can render a "not found" state without a
 * network call.
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type { InvitePreview } from '@/data/invitePreview';

export interface UseInvitePreviewResult {
  preview: InvitePreview | null;
  isLoading: boolean;
  error: unknown;
  /** True when the backend returned a NOT_FOUND for the token. */
  isNotFound: boolean;
  /** True when the backend rate-limited the caller (429). */
  isRateLimited: boolean;
}

export function useInvitePreview(token: string | null): UseInvitePreviewResult {
  const query = useQuery({
    queryKey: ['invitePreview', token],
    queryFn: async () => {
      // apiFetch throws on non-2xx with the server error message in err.message.
      // Status-specific discrimination happens below via message regex —
      // we don't wrap here because we just let the rejection propagate to
      // React Query.
      return apiFetch<InvitePreview>(
        `/api/v1/invites/${encodeURIComponent(token ?? '')}/preview`,
        null,
      );
    },
    enabled: !!token,
    retry: false,
  });

  const message = (query.error as Error | undefined)?.message ?? '';
  // apiFetch throws an Error whose message is the server-provided error
  // message; the generated messages in invites.ts are stable enough to
  // discriminate on. 104d.2 keeps this narrow — if we need richer error
  // metadata later, apiFetch can be extended to thread the status code.
  const isNotFound = /not found/i.test(message);
  const isRateLimited = /too many preview requests/i.test(message);

  return {
    preview: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    isNotFound,
    isRateLimited,
  };
}
